/**
 * The walk's reads, declared once for the screens that stand a reviewer on a leaf (the kind walk and the held tray's
 * sheet): a sheet, the next leaf of a category from a place, and where a leaf stands before it. Every call is
 * admin's generated client; a category is the server's fact (a question's link names it), never a table here.
 */
import { useQuery } from '@tanstack/react-query';
import { adminClient } from './admin';
import { dataOrThrow } from './typed';
import type { components } from './types/api';
import type { BookName } from './page';

export type KindName = components['schemas']['KindName'];
export type WalkPage = components['schemas']['WalkPage'];
export type NextInKind = components['schemas']['NextInKind'];

/** One sheet's read: the key every screen that shows the sheet shares, so the walk and the held tray read it once. */
export const walkKey = (book: BookName, page: number) => ['review', 'walk', book, page] as const;

/** One printed sheet of a book: its image, its extent, its leaves with their readings, its standing fixes. */
export function useWalkPage(book: BookName, page: number) {
  return useQuery<WalkPage>({
    queryKey: walkKey(book, page),
    queryFn: async () => {
      const response = await adminClient.GET('/api/review/walk', { params: { query: { book, page } } });
      return dataOrThrow(response, 'the page');
    },
  });
}

/** Where a walk of a kind starts reading from: the kind's very first leaf, or the leaf after a place. */
export type WalkFrom = { readonly kind: 'start' } | { readonly kind: 'after'; readonly page: number; readonly ordinal: number };

/** The leaf a walk of a kind reaches from a place: the leaf, the catalog address, or the kind's end. */
export async function readNextInKind(at: { book: BookName; kind: KindName; from: WalkFrom }): Promise<NextInKind> {
  const { book, kind, from } = at;
  const response =
    from.kind === 'start'
      ? await adminClient.GET('/api/review/walk/first', { params: { query: { book, kind } } })
      : await adminClient.GET('/api/review/walk/next', { params: { query: { book, kind, page: from.page, ordinal: from.ordinal } } });
  return dataOrThrow(response, 'the next leaf');
}

/** The last ordinal a sheet can hold, for asking what stands before a sheet's first leaf (a bound, not a count). */
const LAST_ORDINAL = 1_000_000;

/** The place just before a leaf: the leaf before it on its sheet, the end of the sheet before, or the start of the book. */
export function placeBefore(leaf: { page: number; ordinal: number }): WalkFrom {
  if (leaf.ordinal > 0) return { kind: 'after', page: leaf.page, ordinal: leaf.ordinal - 1 };
  if (leaf.page > 0) return { kind: 'after', page: leaf.page - 1, ordinal: LAST_ORDINAL };
  return { kind: 'start' };
}
